"use client";

import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'en' | 'es';

interface Translations {
  // Header
  nav_services: string;
  nav_ai_agents: string;
  nav_use_cases: string;
  nav_process: string;
  nav_about: string;
  nav_cta: string;

  // Hero
  hero_label: string;
  hero_headline_1: string;
  hero_headline_accent: string;
  hero_headline_2: string;
  hero_sub: string;
  hero_cta_primary: string;
  hero_cta_secondary: string;
  hero_card1_title: string;
  hero_card1_sub: string;
  hero_card1_tasks: string;
  hero_card1_efficiency: string;
  hero_card2_label: string;
  hero_card2_title: string;
  hero_card2_step1: string;
  hero_card2_step2: string;
  hero_card2_step3: string;
  hero_card3_label: string;
  hero_card3_sub: string;

  // Services
  services_label: string;
  services_headline_1: string;
  services_headline_accent: string;
  services_headline_2: string;
  services_sub: string;
  services_popular: string;
  services_learn_more: string;
  service1_title: string;
  service1_desc: string;
  service2_title: string;
  service2_desc: string;
  service3_title: string;
  service3_desc: string;
  service4_title: string;
  service4_desc: string;
  service5_title: string;
  service5_desc: string;

  // AI Agents
  agents_label: string;
  agents_headline_1: string;
  agents_headline_accent: string;
  agents_headline_2: string;
  agents_sub: string;
  agents_cap1: string;
  agents_cap2: string;
  agents_cap3: string;
  agents_cap4: string;
  agents_cap5: string;
  agents_cap6: string;
  agents_node1: string;
  agents_node2: string;
  agents_node3: string;
  agents_node4: string;
  agents_node5: string;
  agents_node6: string;

  // Use Cases
  usecases_label: string;
  usecases_headline_1: string;
  usecases_headline_accent: string;
  usecases_sub: string;
  uc1_title: string;
  uc1_desc: string;
  uc1_tag: string;
  uc1_metric_label: string;
  uc2_title: string;
  uc2_desc: string;
  uc2_tag: string;
  uc2_metric_label: string;
  uc3_title: string;
  uc3_desc: string;
  uc3_tag: string;
  uc3_metric_label: string;
  uc4_title: string;
  uc4_desc: string;
  uc4_tag: string;
  uc4_metric_label: string;
  uc5_title: string;
  uc5_desc: string;
  uc5_tag: string;
  uc5_metric_label: string;
  usecases_see_how: string;

  // Process
  process_label: string;
  process_headline_1: string;
  process_headline_accent: string;
  process_sub: string;
  step1_title: string;
  step1_desc: string;
  step2_title: string;
  step2_desc: string;
  step3_title: string;
  step3_desc: string;
  step4_title: string;
  step4_desc: string;

  // Why Devros
  why_label: string;
  why_headline_1: string;
  why_headline_accent: string;
  why_headline_2: string;
  why_sub: string;
  benefit1_title: string;
  benefit1_desc: string;
  benefit2_title: string;
  benefit2_desc: string;
  benefit3_title: string;
  benefit3_desc: string;
  benefit4_title: string;
  benefit4_desc: string;
  social_label: string;
  social_headline_1: string;
  social_headline_accent: string;
  social_sub: string;
  t1_quote: string;
  t1_name: string;
  t1_role: string;
  t2_quote: string;
  t2_name: string;
  t2_role: string;
  t3_quote: string;
  t3_name: string;
  t3_role: string;

  // CTA
  cta_label: string;
  cta_headline_1: string;
  cta_headline_accent: string;
  cta_headline_2: string;
  cta_sub: string;
  cta_button: string;
  cta_trust1: string;
  cta_trust2: string;
  cta_trust3: string;
  cta_location: string;

  // Footer
  footer_copyright: string;
  footer_privacy: string;
  footer_terms: string;
  footer_contact_label: string;
}

const en: Translations = {
  nav_services: 'Services',
  nav_ai_agents: 'AI Agents',
  nav_use_cases: 'Use Cases',
  nav_process: 'Process',
  nav_about: 'About',
  nav_cta: 'Book Free Consultation',

  hero_label: 'AI Automation for Business',
  hero_headline_1: 'We implement',
  hero_headline_accent: 'AI',
  hero_headline_2: 'in your business',
  hero_sub: 'We design and deploy custom AI agents and automations that streamline operations, reduce manual work, and unlock growth.',
  hero_cta_primary: 'Book Free Consultation',
  hero_cta_secondary: 'Explore Our Solutions',
  hero_card1_title: 'AI Agent Active',
  hero_card1_sub: 'Processing tasks',
  hero_card1_tasks: 'Tasks completed',
  hero_card1_efficiency: 'Efficiency',
  hero_card2_label: 'Automation Running',
  hero_card2_title: 'Lead Qualification',
  hero_card2_step1: 'Research',
  hero_card2_step2: 'Score',
  hero_card2_step3: 'Route',
  hero_card3_label: 'Time saved / week',
  hero_card3_sub: '↑ from manual work',

  services_label: 'What We Do',
  services_headline_1: 'AI Automation Built For',
  services_headline_accent: 'Real Business',
  services_headline_2: 'Problems',
  services_sub: 'Devros helps companies automate workflows using AI agents, automation systems, and low-code solutions — tailored to how your business actually operates.',
  services_popular: 'Most Popular',
  services_learn_more: 'Learn more',
  service1_title: 'Custom Business Automations',
  service1_desc: 'End-to-end workflow automation tailored to your exact operations — no generic templates, no wasted time.',
  service2_title: 'AI Agents for Operations',
  service2_desc: 'Deploy intelligent agents that handle research, reporting, data analysis, and customer communication autonomously.',
  service3_title: 'AI Consulting',
  service3_desc: 'Strategic guidance on AI adoption — from identifying automation opportunities to building your AI roadmap.',
  service4_title: 'Airtable Solutions',
  service4_desc: 'Transform Airtable into a powerful operational hub with custom bases, automations, and AI-connected workflows.',
  service5_title: 'AI Training for Teams',
  service5_desc: 'Hands-on workshops and training programs that help your team confidently adopt and leverage AI tools daily.',

  agents_label: 'AI Workforce',
  agents_headline_1: 'Your',
  agents_headline_accent: 'AI Workforce',
  agents_headline_2: 'Always On',
  agents_sub: 'AI agents are autonomous software workers that perform complex tasks without human intervention — 24/7, at scale, without errors.',
  agents_cap1: 'Research & Analysis',
  agents_cap2: 'Report Generation',
  agents_cap3: 'Workflow Automation',
  agents_cap4: 'Data Processing',
  agents_cap5: 'Customer Communication',
  agents_cap6: 'Regulatory Monitoring',
  agents_node1: 'Research',
  agents_node2: 'Reports',
  agents_node3: 'Data',
  agents_node4: 'Comm',
  agents_node5: 'Monitor',
  agents_node6: 'Automate',

  usecases_label: 'Real Impact',
  usecases_headline_1: 'Built for How Business',
  usecases_headline_accent: 'Actually Works',
  usecases_sub: 'From sales pipelines to compliance monitoring — AI agents tackle the work that drains your team\'s time every day.',
  uc1_title: 'Automated Research & Reporting',
  uc1_desc: 'AI agents pull data from multiple sources, analyze trends, and generate structured reports — without a single manual step.',
  uc1_tag: 'Research',
  uc1_metric_label: 'per report',
  uc2_title: 'Regulatory & Legislation Monitoring',
  uc2_desc: 'Continuously monitor regulatory changes, flag relevant updates, and summarize impacts for your compliance team.',
  uc2_tag: 'Compliance',
  uc2_metric_label: 'coverage',
  uc3_title: 'Sales Lead Qualification',
  uc3_desc: 'Automatically research prospects, score leads based on your criteria, and route hot leads directly to your sales team.',
  uc3_tag: 'Sales',
  uc3_metric_label: 'conversion rate',
  uc4_title: 'Operations Workflow Automation',
  uc4_desc: 'Map your operational processes and automate repetitive steps — approvals, notifications, data entry, and handoffs.',
  uc4_tag: 'Operations',
  uc4_metric_label: 'time saved',
  uc5_title: 'Customer Support Automation',
  uc5_desc: 'Deploy AI agents that handle tier-1 support queries, route complex issues, and maintain consistent response quality.',
  uc5_tag: 'Support',
  uc5_metric_label: 'availability',
  usecases_see_how: 'See how it works',

  process_label: 'How It Works',
  process_headline_1: 'From Idea to',
  process_headline_accent: 'Live Automation',
  process_sub: 'A clear, structured process that gets your AI solution running in weeks — not months.',
  step1_title: 'Discovery',
  step1_desc: 'We map your current workflows, identify automation opportunities, and define success metrics that matter to your business.',
  step2_title: 'Design',
  step2_desc: 'We architect the AI agent or automation system — selecting tools, designing flows, and mapping data connections.',
  step3_title: 'Implementation',
  step3_desc: 'We build and deploy your custom solution — integrating with your existing systems, testing thoroughly, and training your team.',
  step4_title: 'Optimization',
  step4_desc: 'We monitor performance, gather feedback, and continuously refine your automations to maximize efficiency and ROI.',

  why_label: 'Why Devros',
  why_headline_1: 'The Difference Between',
  why_headline_accent: 'Generic AI',
  why_headline_2: 'and What Actually Works',
  why_sub: 'Most AI tools are built for everyone, which means they\'re optimized for no one. We build solutions that fit your business exactly — and we stay until they deliver results.',
  benefit1_title: 'Custom-Built for Your Business',
  benefit1_desc: 'No off-the-shelf templates. Every automation is engineered around your specific workflows, data, and business logic.',
  benefit2_title: 'Fast Deployment with Low-Code',
  benefit2_desc: 'We leverage best-in-class low-code tools to deliver production-ready automations in weeks, not quarters.',
  benefit3_title: 'Integrated with Your Stack',
  benefit3_desc: 'Seamlessly connects with your existing tools — CRM, ERP, databases, APIs — no rip-and-replace required.',
  benefit4_title: 'Focused on Real ROI',
  benefit4_desc: 'We measure success in hours saved, costs reduced, and revenue generated — not just features shipped.',
  social_label: 'Social Proof',
  social_headline_1: 'Companies Are Starting to Work',
  social_headline_accent: 'Smarter With AI',
  social_sub: 'Real results from businesses that made the shift.',
  t1_quote: 'Devros automated our cold email outreach process. Our email response rate surged from 2.5% to 11.8%. We’re now booking 3x more qualified discovery calls every month while saving over 15 hours of manual work each week.',
  t1_name: 'Melissa Vazart',
  t1_role: 'Founder · Lab20',
  t2_quote: 'Implementing Devros solutions has been a major milestone for our operations. It has streamlined our workflow, slashed the time spent on repetitive tasks, and given us tighter control over our daily processes—allowing us to refocus our efforts on high-value, strategic initiatives.',
  t2_name: 'Alejandro Arcila',
  t2_role: 'Founder · Arcila & Sotomayor',
  t3_quote: 'Devros completely transformed how we handle our billing and payment operations. By automating our repetitive invoicing, payment tracking, and reconciliation workflows, we reduced billing errors to near zero and cut our monthly processing time by 60%.',
  t3_name: 'Fabio Espinosa',
  t3_role: 'CEO · BIM SAS',

  cta_label: 'Get Started',
  cta_headline_1: 'Discover How AI Can',
  cta_headline_accent: 'Transform',
  cta_headline_2: 'Your Operations',
  cta_sub: 'Book a free 30-minute consultation. We\'ll map your biggest workflow bottlenecks and show you exactly where AI can deliver immediate impact.',
  cta_button: 'Schedule Your Free AI Consultation',
  cta_trust1: 'No commitment required',
  cta_trust2: '30-minute session',
  cta_trust3: 'Tailored to your business',
  cta_location: 'Remote-first · Global clients',

  footer_copyright: '© 2026 Devros AI. All rights reserved.',
  footer_privacy: 'Privacy',
  footer_terms: 'Terms',
  footer_contact_label: 'Contact',
};

const es: Translations = {
  nav_services: 'Servicios',
  nav_ai_agents: 'Agentes IA',
  nav_use_cases: 'Casos de Uso',
  nav_process: 'Proceso',
  nav_about: 'Nosotros',
  nav_cta: 'Consulta Gratuita',

  hero_label: 'Automatización IA para Empresas',
  hero_headline_1: 'Implementamos',
  hero_headline_accent: 'IA',
  hero_headline_2: 'en tu empresa',
  hero_sub: 'Diseñamos e implementamos agentes de IA y automatizaciones personalizadas que optimizan operaciones, reducen el trabajo manual y desbloquean el crecimiento.',
  hero_cta_primary: 'Agenda Consulta Gratuita',
  hero_cta_secondary: 'Explorar Soluciones',
  hero_card1_title: 'Agente IA Activo',
  hero_card1_sub: 'Procesando tareas',
  hero_card1_tasks: 'Tareas completadas',
  hero_card1_efficiency: 'Eficiencia',
  hero_card2_label: 'Automatización Activa',
  hero_card2_title: 'Calificación de Leads',
  hero_card2_step1: 'Investigar',
  hero_card2_step2: 'Puntuar',
  hero_card2_step3: 'Enrutar',
  hero_card3_label: 'Tiempo ahorrado / semana',
  hero_card3_sub: '↑ de trabajo manual',

  services_label: 'Lo Que Hacemos',
  services_headline_1: 'Automatización IA Para',
  services_headline_accent: 'Problemas Reales',
  services_headline_2: 'de Negocio',
  services_sub: 'Devros ayuda a las empresas a automatizar flujos de trabajo con agentes de IA, sistemas de automatización y soluciones low-code — adaptadas a cómo opera tu negocio.',
  services_popular: 'Más Popular',
  services_learn_more: 'Saber más',
  service1_title: 'Automatizaciones Empresariales',
  service1_desc: 'Automatización de flujos de trabajo de extremo a extremo adaptada a tus operaciones exactas — sin plantillas genéricas, sin tiempo perdido.',
  service2_title: 'Agentes IA para Operaciones',
  service2_desc: 'Despliega agentes inteligentes que manejan investigación, reportes, análisis de datos y comunicación con clientes de forma autónoma.',
  service3_title: 'Consultoría IA',
  service3_desc: 'Orientación estratégica en adopción de IA — desde identificar oportunidades de automatización hasta construir tu hoja de ruta de IA.',
  service4_title: 'Soluciones Airtable',
  service4_desc: 'Transforma Airtable en un centro operativo potente con bases personalizadas, automatizaciones y flujos conectados con IA.',
  service5_title: 'Capacitación IA para Equipos',
  service5_desc: 'Talleres y programas de formación prácticos que ayudan a tu equipo a adoptar y aprovechar herramientas de IA con confianza.',

  agents_label: 'Fuerza Laboral IA',
  agents_headline_1: 'Tu',
  agents_headline_accent: 'Fuerza Laboral IA',
  agents_headline_2: 'Siempre Activa',
  agents_sub: 'Los agentes de IA son trabajadores de software autónomos que realizan tareas complejas sin intervención humana — 24/7, a escala, sin errores.',
  agents_cap1: 'Investigación y Análisis',
  agents_cap2: 'Generación de Reportes',
  agents_cap3: 'Automatización de Flujos',
  agents_cap4: 'Procesamiento de Datos',
  agents_cap5: 'Comunicación con Clientes',
  agents_cap6: 'Monitoreo Regulatorio',
  agents_node1: 'Investigar',
  agents_node2: 'Reportes',
  agents_node3: 'Datos',
  agents_node4: 'Comun.',
  agents_node5: 'Monitor',
  agents_node6: 'Automatizar',

  usecases_label: 'Impacto Real',
  usecases_headline_1: 'Construido para Cómo',
  usecases_headline_accent: 'Funciona el Negocio',
  usecases_sub: 'Desde pipelines de ventas hasta monitoreo de cumplimiento — los agentes de IA abordan el trabajo que consume el tiempo de tu equipo cada día.',
  uc1_title: 'Investigación y Reportes Automatizados',
  uc1_desc: 'Los agentes de IA extraen datos de múltiples fuentes, analizan tendencias y generan reportes estructurados — sin un solo paso manual.',
  uc1_tag: 'Investigación',
  uc1_metric_label: 'por reporte',
  uc2_title: 'Monitoreo Regulatorio y Legislativo',
  uc2_desc: 'Monitorea continuamente cambios regulatorios, marca actualizaciones relevantes y resume impactos para tu equipo de cumplimiento.',
  uc2_tag: 'Cumplimiento',
  uc2_metric_label: 'cobertura',
  uc3_title: 'Calificación de Leads de Ventas',
  uc3_desc: 'Investiga prospectos automáticamente, puntúa leads según tus criterios y enruta leads calientes directamente a tu equipo de ventas.',
  uc3_tag: 'Ventas',
  uc3_metric_label: 'tasa de conversión',
  uc4_title: 'Automatización de Flujos Operativos',
  uc4_desc: 'Mapea tus procesos operativos y automatiza pasos repetitivos — aprobaciones, notificaciones, entrada de datos y transferencias.',
  uc4_tag: 'Operaciones',
  uc4_metric_label: 'tiempo ahorrado',
  uc5_title: 'Automatización de Soporte al Cliente',
  uc5_desc: 'Despliega agentes de IA que manejan consultas de soporte de nivel 1, enrutan problemas complejos y mantienen calidad de respuesta consistente.',
  uc5_tag: 'Soporte',
  uc5_metric_label: 'disponibilidad',
  usecases_see_how: 'Ver cómo funciona',

  process_label: 'Cómo Funciona',
  process_headline_1: 'De la Idea a la',
  process_headline_accent: 'Automatización en Vivo',
  process_sub: 'Un proceso claro y estructurado que pone en marcha tu solución de IA en semanas — no meses.',
  step1_title: 'Descubrimiento',
  step1_desc: 'Mapeamos tus flujos de trabajo actuales, identificamos oportunidades de automatización y definimos métricas de éxito relevantes para tu negocio.',
  step2_title: 'Diseño',
  step2_desc: 'Diseñamos el agente de IA o sistema de automatización — seleccionando herramientas, diseñando flujos y mapeando conexiones de datos.',
  step3_title: 'Implementación',
  step3_desc: 'Construimos y desplegamos tu solución personalizada — integrando con tus sistemas existentes, probando exhaustivamente y capacitando a tu equipo.',
  step4_title: 'Optimización',
  step4_desc: 'Monitoreamos el rendimiento, recopilamos retroalimentación y refinamos continuamente tus automatizaciones para maximizar eficiencia y ROI.',

  why_label: 'Por Qué Devros',
  why_headline_1: 'La Diferencia Entre',
  why_headline_accent: 'IA Genérica',
  why_headline_2: 'y Lo Que Realmente Funciona',
  why_sub: 'La mayoría de las herramientas de IA están construidas para todos, lo que significa que no están optimizadas para nadie. Construimos soluciones que se adaptan exactamente a tu negocio — y nos quedamos hasta que entreguen resultados.',
  benefit1_title: 'Construido para Tu Empresa',
  benefit1_desc: 'Sin plantillas genéricas. Cada automatización está diseñada alrededor de tus flujos de trabajo, datos y lógica de negocio específicos.',
  benefit2_title: 'Despliegue Rápido con Low-Code',
  benefit2_desc: 'Aprovechamos las mejores herramientas low-code para entregar automatizaciones listas para producción en semanas, no trimestres.',
  benefit3_title: 'Integrado con Tu Stack',
  benefit3_desc: 'Se conecta perfectamente con tus herramientas existentes — CRM, ERP, bases de datos, APIs — sin necesidad de reemplazar nada.',
  benefit4_title: 'Enfocado en ROI Real',
  benefit4_desc: 'Medimos el éxito en horas ahorradas, costos reducidos e ingresos generados — no solo en funcionalidades entregadas.',
  social_label: 'Prueba Social',
  social_headline_1: 'Las Empresas Están Empezando a Trabajar',
  social_headline_accent: 'Más Inteligente con IA',
  social_sub: 'Resultados reales de empresas que hicieron el cambio.',
  t1_quote: 'Devros automatizó todo nuestro proceso de prospección por correo frío. Nuestra tasa de respuesta pasó del 2,5% al 11,8%; ahora estamos agendando el triple de llamadas de valoración calificadas cada mes y, de paso, nos ahorramos más de 15 horas de trabajo manual a la semana.',
  t1_name: 'Melissa Vazart',
  t1_role: 'Founder · Lab20',
  t2_quote: 'La implementación de las soluciones de Devros ha representado un avance significativo dentro de nuestro proceso operativo, optimizando el flujo de trabajo y permitiéndonos reducir tiempos en tareas repetitivas y operativas, además de tener un mayor control dentro de nuestras operaciones, para enfocar nuestros esfuerzos en actividades de mayor valor estratégico.',
  t2_name: 'Alejandro Arcila',
  t2_role: 'Founder · Arcila & Sotomayor',
  t3_quote: 'Devros transformó por completo la forma en que manejamos la facturación y los pagos. Al automatizar los procesos repetitivos de cobro, seguimiento y conciliación, redujimos los errores de facturación casi a cero y recortamos el tiempo de procesamiento mensual en un 60%.',
  t3_name: 'Fabio Espinosa',
  t3_role: 'Gerente General · BIM SAS',

  cta_label: 'Comenzar',
  cta_headline_1: 'Descubre Cómo la IA Puede',
  cta_headline_accent: 'Transformar',
  cta_headline_2: 'Tus Operaciones',
  cta_sub: 'Reserva una consulta gratuita de 30 minutos. Mapearemos tus mayores cuellos de botella y te mostraremos exactamente dónde la IA puede generar impacto inmediato.',
  cta_button: 'Agenda Tu Consulta IA Gratuita',
  cta_trust1: 'Sin compromiso',
  cta_trust2: 'Sesión de 30 minutos',
  cta_trust3: 'Adaptado a tu empresa',
  cta_location: 'Trabajo remoto · Clientes globales',

  footer_copyright: '© 2026 Devros AI. Todos los derechos reservados.',
  footer_privacy: 'Privacidad',
  footer_terms: 'Términos',
  footer_contact_label: 'Contacto',
};

const translations: Record<Language, Translations> = { en, es };

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: keyof Translations) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: (key) => en[key],
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('en');
  const t = (key: keyof Translations): string => translations[language][key];
  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
